import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Settings2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import StepIndicator from "@/components/StepIndicator";
import ProgressBar from "@/components/ProgressBar";
import PreviewPanel from "@/components/PreviewPanel";
import { generateSceneDescriptions, generateVoiceScript } from "@/services/aiService";
import { fetchImageForScene } from "@/services/imageService";
import { ttsService } from "@/services/ttsService";

interface GenerationState {
  currentStep: number;
  percentage: number;
  elapsedTime: number;
  estimatedTime: number;
}

export default function Studio() {
  const [title, setTitle] = useState("");
  const [script, setScript] = useState("");
  const [language, setLanguage] = useState("ar");
  const [duration, setDuration] = useState("short");
  const [isGenerating, setIsGenerating] = useState(false);
  const [videoId, setVideoId] = useState<string | null>(null);
  const [generation, setGeneration] = useState<GenerationState>({
    currentStep: 0,
    percentage: 0,
    elapsedTime: 0,
    estimatedTime: 60,
  });

  const languageLabel: Record<string, string> = {
    ar: "العربية",
    en: "الإنجليزية",
    fr: "الفرنسية",
    es: "الإسبانية",
  };

  const durationLabel: Record<string, string> = {
    short: "قصير (1-3 دقائق)",
    medium: "متوسط (3-10 دقائق)",
    long: "طويل (10+ دقائق)",
  };

  const steps = [
    {
      id: 1,
      label: "تحليل النص",
      description: "معالجة السيناريو وتحليل المحتوى",
      status:
        generation.currentStep > 0
          ? "completed"
          : generation.currentStep === 0
            ? "active"
            : "pending",
    },
    {
      id: 2,
      label: "إنشاء الصور",
      description: "توليد مشاهد الفيديو باستخدام AI",
      status:
        generation.currentStep > 1
          ? "completed"
          : generation.currentStep === 1
            ? "active"
            : "pending",
    },
    {
      id: 3,
      label: "تركيب الصوت",
      description: "إضافة التعليق الصوتي والموسيقى",
      status:
        generation.currentStep > 2
          ? "completed"
          : generation.currentStep === 2
            ? "active"
            : "pending",
    },
    {
      id: 4,
      label: "إضافة الترجمات",
      description: "دمج النصوص والترجمات",
      status:
        generation.currentStep > 3
          ? "completed"
          : generation.currentStep === 3
            ? "active"
            : "pending",
    },
    {
      id: 5,
      label: "التشطيب النهائي",
      description: "معالجة الفيديو والتحسينات النهائية",
      status:
        generation.currentStep > 4
          ? "completed"
          : generation.currentStep === 4
            ? "active"
            : "pending",
    },
  ];

  useEffect(() => {
    if (!isGenerating) return;

    const interval = setInterval(() => {
      setGeneration((prev) => {
        const newElapsed = prev.elapsedTime + 1;
        const newPercentage = Math.min(
          100,
          Math.floor((newElapsed / prev.estimatedTime) * 100)
        );
        const newStep = Math.floor((newPercentage / 100) * 5);

        if (newPercentage >= 100) {
          clearInterval(interval);
          setIsGenerating(false);
          setVideoId(`video_${Date.now()}`);
          toast.success("تم إنشاء الفيلم بنجاح! ✨");
          return prev;
        }

        return {
          ...prev,
          elapsedTime: newElapsed,
          percentage: newPercentage,
          currentStep: newStep,
        };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isGenerating]);

  const handleGenerate = async () => {
    if (!title.trim() || !script.trim()) {
      toast.error("أضف عنوان والنص أولاً");
      return;
    }

    setIsGenerating(true);
    const estimatedSeconds = duration === "short" ? 60 : duration === "medium" ? 120 : 180;

    setGeneration({
      currentStep: 0,
      percentage: 0,
      elapsedTime: 0,
      estimatedTime: estimatedSeconds,
    });

    try {
      toast.loading("جاري تحليل النص...");

      // Step 1: تحليل النص وإنشاء المشاهد
      const scenes = await generateSceneDescriptions({
        script,
        language,
        duration,
      });

      updateProgress(1, estimatedSeconds);
      toast.loading("جاري إنشاء الصور...");

      // Step 2: جلب الصور
      const images = await Promise.all(
        scenes.slice(0, 5).map((scene) => fetchImageForScene(scene))
      );

      updateProgress(2, estimatedSeconds);
      toast.loading("جاري إنشاء السيناريو الصوتي...");

      // Step 3: إنشاء السيناريو الصوتي
      const voiceScript = await generateVoiceScript({
        script,
        language,
        duration,
      });

      updateProgress(3, estimatedSeconds);
      toast.loading("جاري إنشاء الصوت...");

      // Step 4: تولید الصوت
      await ttsService.speak(voiceScript, language);

      updateProgress(4, estimatedSeconds);
      toast.loading("جاري التشطيب النهائي...");

      // Step 5: التشطيب النهائي
      updateProgress(5, estimatedSeconds);

      setVideoId(`video_${Date.now()}`);
      setIsGenerating(false);
      toast.success("تم إنشاء الفيلم بنجاح! ✨");
    } catch (error) {
      console.error("Error generating video:", error);
      toast.error("حدث خطأ أثناء إنشاء الفيلم");
      setIsGenerating(false);
    }
  };

  const updateProgress = (step: number, estimatedTime: number) => {
    const percentage = Math.floor((step / 5) * 100);
    const elapsedTime = (step / 5) * estimatedTime;

    setGeneration((prev) => ({
      ...prev,
      currentStep: step,
      percentage,
      elapsedTime: Math.floor(elapsedTime),
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      {/* Header */}
      <div className="border-b border-slate-700 bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link
            to="/"
            className={`flex items-center gap-2 ${isGenerating ? "opacity-50 cursor-not-allowed" : "hover:opacity-80"}`}
            onClick={(e) => isGenerating && e.preventDefault()}
          >
            <ArrowLeft className="w-5 h-5 text-slate-400" />
            <span className="text-slate-300">رجوع</span>
          </Link>
          <h1 className="text-xl font-bold text-white">استوديو الأفلام</h1>
          <div className="w-20" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Input Panel */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="p-6 border-slate-700 bg-slate-800/50">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Settings2 className="w-5 h-5 text-cyan-400" />
                تفاصيل الفيلم
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    عنوان الفيلم
                  </label>
                  <Input
                    placeholder="مثال: قصة الحب الأبدية"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={isGenerating}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    النص / السيناريو
                  </label>
                  <Textarea
                    placeholder="اكتب نص الفيلم هنا..."
                    value={script}
                    onChange={(e) => setScript(e.target.value)}
                    disabled={isGenerating}
                    rows={6}
                    className="bg-slate-700 border-slate-600 text-white resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      اللغة
                    </label>
                    <Select value={language} onValueChange={setLanguage} disabled={isGenerating}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ar">العربية</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="fr">Français</SelectItem>
                        <SelectItem value="es">Español</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      المدة
                    </label>
                    <Select value={duration} onValueChange={setDuration} disabled={isGenerating}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="short">قصير (1-3 دقائق)</SelectItem>
                        <SelectItem value="medium">متوسط (3-10 دقائق)</SelectItem>
                        <SelectItem value="long">طويل (10+ دقائق)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="w-full bg-cyan-500 hover:bg-cyan-600 disabled:bg-slate-600 text-white font-semibold h-11"
                >
                  {isGenerating ? "جاري الإنشاء..." : "إنشاء الفيلم"}
                </Button>
              </div>
            </Card>
          </div>

          {/* Steps and Progress Panel */}
          <div className="lg:col-span-1 space-y-6">
            {isGenerating && (
              <>
                <Card className="p-6 border-slate-700 bg-slate-800/50">
                  <ProgressBar
                    percentage={generation.percentage}
                    estimatedTime={generation.estimatedTime}
                    elapsedTime={generation.elapsedTime}
                  />
                </Card>

                <Card className="p-6 border-slate-700 bg-slate-800/50">
                  <h2 className="text-lg font-semibold text-white mb-4">
                    مراحل الإنشاء
                  </h2>
                  <StepIndicator steps={steps} />
                </Card>
              </>
            )}
          </div>

          {/* Preview Panel */}
          <div className="lg:col-span-1">
            <PreviewPanel
              data={{
                title,
                language: languageLabel[language],
                duration: durationLabel[duration],
                hasScript: isGenerating && generation.currentStep > 0,
                hasVoice: isGenerating && generation.currentStep > 1,
                hasSubtitles: isGenerating && generation.currentStep > 2,
              }}
              isGenerating={isGenerating}
              videoId={videoId}
            />
          </div>
        </div>
      </div>
    </div>
  );
}